import { useEffect, useRef, useState } from "react";

import classNames from "classnames/bind";
import { ALPHADIGITS_REGEX, PHONE_NUMBER_REGEX } from "lib/constants";
import { CustomSignUpBlockSchema } from "lib/schemas/schemas";
import { ResolvedCustomSignUpBlock } from "resolvers/data/customSignUpBlockResolver";
import { logError } from "utils/logger";
import { z } from "zod";

import DynamicForm, { DynamicFormProps } from "../DynamicForm";
import RichText from "../RichText";
import UIIcon from "../UIIcon";
import styles from "./CustomSignUpBlock.module.scss";
import useCustomSignUpBlockFormSubmission from "./useCustomSignUpBlockFormSubmission";

const cx = classNames.bind(styles);

type CustomSignUpBlockType = z.infer<typeof CustomSignUpBlockSchema>;

const formFields: DynamicFormProps<
    CustomSignUpBlockType,
    typeof CustomSignUpBlockSchema
>["fieldData"] = [
    {
        fieldType: "input",
        className: "text",
        name: "registration",
        label: "Car registration:",
        type: "text",
        maxLength: 8,
        regex: ALPHADIGITS_REGEX,
        validationRule: CustomSignUpBlockSchema.shape.registration,
    },
    {
        fieldType: "input",
        className: "number",
        name: "mileage",
        label: "Current mileage:",
        type: "number",
        inputMode: "numeric",
        pattern: "\d*",
        autoComplete: "vehicle-mileage",
        validationRule: CustomSignUpBlockSchema.shape.mileage,
    },
    {
        fieldType: "select",
        className: "text",
        name: "desired_warranty",
        label: "Desired warranty:",
        type: "text",
        validationRule: CustomSignUpBlockSchema.shape.desired_warranty,
        selectOptions: [
            { label: "Please select", value: "" },
            {
                label: "1-year Motorpoint Extended Warranty",
                value: "1-year Motorpoint Extended Warranty",
            },
            {
                label: "2-year Motorpoint Extended Warranty",
                value: "2-year Motorpoint Extended Warranty",
            },
            {
                label: "3-year Motorpoint Extended Warranty",
                value: "3-year Motorpoint Extended Warranty",
            },
            {
                label: "4-year Motorpoint Extended Warranty",
                value: "4-year Motorpoint Extended Warranty",
            },
        ],
    },
    {
        fieldType: "input",
        className: "text",
        name: "phone",
        label: "Mobile number:",
        helperText:
            "We require a valid UK mobile number. In order to purchase, a payment link will be sent via SMS",
        type: "tel",
        autoComplete: "tel-national",
        minLength: 11,
        maxLength: 13,
        regex: PHONE_NUMBER_REGEX,
        validationRule: CustomSignUpBlockSchema.shape.phone,
    },
    {
        fieldType: "input",
        className: "text",
        name: "email",
        label: "Email address:",
        type: "text",
        autoComplete: "email",
        validationRule: CustomSignUpBlockSchema.shape.email,
    },
    {
        fieldType: "marketingconsent",
        name: "marketingConsent",
        primaryText: "Yes, I’d like to receive the latest offers and recommendations by email.",
        validationRule: CustomSignUpBlockSchema.shape.marketingConsent,
    },
];

export function CustomSignUpBlock(customSignUpFormProps: ResolvedCustomSignUpBlock) {
    const recaptchaTokenRef = useRef<HTMLInputElement>(null);
    const formContainerRef = useRef<HTMLDivElement>(null);

    const {
        header,
        responseCopy,
        customSignUpCopy,
        bannerImagery,
        resubmitText,
        responseImage,
        subCopy,
        responseSubCopy,
        customerEmailTemplateName,
        internalEmailAddress,
        internalEmailTemplateName,
    } = customSignUpFormProps;

    const [isFormVisible, setFormVisible] = useState(true);
    const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
    const { handleFormSubmission, recaptchaToken } =
        useCustomSignUpBlockFormSubmission<CustomSignUpBlockType>();

    const onSubmit = async (formData: CustomSignUpBlockType) => {
        const templates = {
            customerEmailTemplateName,
            internalEmailAddress,
            internalEmailTemplateName,
        };

        try {
            await handleFormSubmission({ formData, templates });
            setFormVisible(true);
        } catch (error) {
            logError("Failed to submit the form", error);
            setFormVisible(false);
        }
    };

    useEffect(() => {
        const loadRecaptchaScript = async () => {
            const script = document.createElement("script");
            script.src = `https://www.google.com/recaptcha/enterprise.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}`;
            script.async = true;
            script.defer = true;

            document.head.appendChild(script);

            return () => {
                document.head.removeChild(script);
            };
        };

        loadRecaptchaScript();
    }, []);

    useEffect(() => {
        if (isSubmitSuccessful) {
            setFormVisible(false);
            if (formContainerRef.current) {
                formContainerRef.current.scrollIntoView({ behavior: "smooth" });
            }
        }
    }, [isSubmitSuccessful]);

    useEffect(() => {
        if (recaptchaToken && recaptchaTokenRef.current) {
            recaptchaTokenRef.current.value = recaptchaToken;
        }
    }, [recaptchaToken]);

    return (
        <div className={cx("container", { isFormVisible })}>
            <div ref={formContainerRef} className={cx("formContainer")}>
                {!isFormVisible && (
                    <UIIcon className={cx("successIcon")} icon="TickOrangeEncircled" />
                )}
                <h2 className={cx("heading")}>{isFormVisible ? header : responseCopy}</h2>
                {isFormVisible ? (
                    <>
                        <RichText className={cx("subCopy")} html={subCopy.html} />
                        <DynamicForm<CustomSignUpBlockType, typeof CustomSignUpBlockSchema>
                            fieldData={formFields}
                            submitButtonCopy="Submit"
                            onSubmit={onSubmit}
                            onSubmitSuccessChange={(s) => setIsSubmitSuccessful(s)}
                            schema={CustomSignUpBlockSchema}
                        />
                        <p className={cx("signupCopy")}>{customSignUpCopy}</p>
                    </>
                ) : (
                    <>
                        <RichText
                            className={cx("subCopyResponse")}
                            html={responseSubCopy.html}
                        ></RichText>
                        {!!resubmitText && (
                            <a className={cx("resubmitLink")} onClick={() => setFormVisible(true)}>
                                <span>{resubmitText}</span>
                            </a>
                        )}
                    </>
                )}
            </div>
            <div className={cx("bannerImageryContainer", { isFormVisible })}>
                <img
                    className={cx("image")}
                    src={isFormVisible ? bannerImagery?.src : responseImage.src}
                    alt={isFormVisible ? bannerImagery?.alt : responseImage.src}
                />
            </div>
        </div>
    );
}
